/**
 * @fileoverview Set Defaults admin page outer level component
 */

import React, { useState } from "react";
import { Upload } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import useAuthStore from "../store/useAuthStore";
import { useDropzone } from "react-dropzone";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import useProForma from "../store/useProForma";
import { useToast } from "components/hooks/use-toast";
import { uploadWithDelete, uploadWithOutDelete } from "../api/uploadapi";
import { saveDefault } from "../api/defaultapi";
import { uploadPhasesCSV } from "../api/phaseApi";
import { getAuthenticatedClient } from "../supabaseClient";
import AddressConfirmationDialog from "../components/create-account/AddressConfirmationDialog";
import { prepareDomicileAddressGroups } from "../utils/siteAddress";

function AdminSetDefaults() {
  const { user } = useAuthStore();
  const [csvFile, setCsvFile] = useState(null);
  const [phasesFile, setPhasesFile] = useState(null);
  const [message, setMessage] = useState("");
  const [messageCharger, setMessageCharger] = useState("");
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [domicileGroups, setDomicileGroups] = useState([]);
  const [isReplacingFleet, setIsReplacingFleet] = useState(false);
  const { fetchAndUpdateFleet } = useProForma();
  const { toast } = useToast();

  const { getRootProps: getFleetRootProps, getInputProps: getFleetInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      setCsvFile(acceptedFiles[0]);
      setAddressDialogOpen(false);
      setDomicileGroups([]);
      setMessage("");
    },
    accept: ".csv",
  });

  const { getRootProps: getPhaseRootProps, getInputProps: getPhaseInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      setPhasesFile(acceptedFiles[0]);
    },
    accept: ".csv",
  });

  const handleReplaceFleet = async () => {
    if (!csvFile) {
      const errorMessage = "Please upload a Vehicle CSV file";
      setMessage(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      return;
    }

    setMessage("");
    try {
      const groups = await prepareDomicileAddressGroups(csvFile);
      setDomicileGroups(groups);
      setAddressDialogOpen(true);
    } catch (error) {
      setMessage(error.message);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const replaceFleetWithAddresses = async (domicileAddressMap) => {
    if (!user) return;

    setIsReplacingFleet(true);
    setMessage("");

    const formData = new FormData();
    formData.append("file", csvFile);
    formData.append("userId", user.id);
    if (domicileAddressMap) {
      formData.append("domicileAddressMap", JSON.stringify(domicileAddressMap));
    }

    try {
      const supabase = getAuthenticatedClient();
      let response;
      const { data: defaultData } = await supabase
        .from("default data")
        .select("id")
        .eq("id", user.id)
        .maybeSingle();

      const idExists = defaultData !== null;
      if (idExists) {
        response = await uploadWithDelete(formData);
      } else {
        response = await uploadWithOutDelete(formData);
      }

      if (response.error) {
        throw new Error(`CSV format is incorrect.\nServer message: ${response.error}`);
      }

      setAddressDialogOpen(false);
      setMessage("");
      fetchAndUpdateFleet();
      toast({
        title: "Success",
        description: "Fleet data replaced successfully",
      });
    } catch (error) {
      setMessage(`Error uploading data: ${error.message}`);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsReplacingFleet(false);
    }
  };

  const uploadCSVFile = async (tableName) => {
    if (!user) return;

    if (tableName === "phases") {
      const formData = new FormData();
      formData.append("file", phasesFile);
      formData.append("userId", user.id);

      try {
        const response = await uploadPhasesCSV(formData, true);

        if (response.error) {
          const errorMessage = `Charger CSV format is incorrect.\nServer message: ${response.error}`;
          throw new Error(errorMessage);
        }
        setMessageCharger(`${response.message}`);
        toast({
          title: "Success",
          description: "Chargers data replaced successfully",
        });
      } catch (error) {
        setMessageCharger(`Error uploading Charger data: ${error.message}`);
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      }
    }
  };

  const handleSaveDefault = async (tableName) => {
    if (!user) return;

    try {
      const response = await saveDefault(user.id, tableName);
      if (response.error) {
        throw new Error("Failed to save defaults");
      }

      toast({
        title: "Success",
        description: `${tableName} defaults saved`,
      });
    } catch (error) {
      toast({
        title: "Error saving defaults",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (!user) {
    return <div>Log in with a user!</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <Card className="max-w-[800px] min-w-[450px] mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl font-semibold text-primary">Set Defaults</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <Tabs
            defaultValue="fleet"
            className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="fleet">Fleet</TabsTrigger>
              <TabsTrigger value="phases">Phases</TabsTrigger>
              <TabsTrigger value="advanced">Advanced Controls</TabsTrigger>
            </TabsList>
            <TabsContent
              value="fleet"
              className="space-y-4">
              <div className="grid gap-4">
                <Button
                  className="w-full"
                  onClick={() => handleSaveDefault("fleet data")}>
                  Save Default Fleet
                </Button>
                <Card
                  className="border-2 border-dashed"
                  {...getFleetRootProps()}>
                  <CardContent className="flex flex-col items-center justify-center py-8 text-center cursor-pointer">
                    <Input {...getFleetInputProps()} />
                    <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
                    {csvFile ? (
                      <p className="text-sm text-muted-foreground mb-1">{csvFile.name}</p>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground mb-1">
                          Drag & Drop Vehicle CSV file here
                        </p>
                        <p className="text-xs text-muted-foreground">or click to select a file</p>
                      </>
                    )}
                    {message && (
                      <p className="text-sm text-red-400 mt-2 whitespace-pre-wrap">
                        {message || messageCharger}
                      </p>
                    )}
                  </CardContent>
                </Card>
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={handleReplaceFleet}
                  disabled={!csvFile || isReplacingFleet}>
                  Replace Fleet
                </Button>
              </div>
            </TabsContent>
            <TabsContent
              value="phases"
              className="space-y-4">
              <Button
                className="w-full"
                onClick={() => handleSaveDefault("phases")}>
                Save Default Chargers
              </Button>
              {/* // Add Phase upload csv file here */}
              <Card
                className="border-2 border-dashed"
                {...getPhaseRootProps()}>
                <CardContent className="flex flex-col items-center justify-center py-8 text-center cursor-pointer">
                  <Input {...getPhaseInputProps()} />
                  <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
                  {phasesFile ? (
                    <p className="text-sm text-muted-foreground mb-1">{phasesFile.name}</p>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground mb-1">
                        Drag & Drop Charger file here
                      </p>
                      <p className="text-xs text-muted-foreground">or click to select a file</p>
                    </>
                  )}
                  {messageCharger && <p className="text-sm text-red-400 mt-2">{messageCharger}</p>}
                </CardContent>
              </Card>
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="secondary"
                    className="w-full">
                    Replace Chargers
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Are you sure you want to do this?</DialogTitle>
                    <DialogDescription>
                      Uploading a new CSV file will replace the current chargers data.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button
                        onClick={() => uploadCSVFile("phases")}
                        type="submit">
                        Confirm
                      </Button>
                    </DialogClose>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </TabsContent>
            <TabsContent
              value="advanced"
              className="space-y-4">
              <Button
                className="w-full"
                onClick={() => handleSaveDefault("advanced controls")}>
                Save Default Advanced Controls (Dashboard)
              </Button>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <AddressConfirmationDialog
        open={addressDialogOpen}
        groups={domicileGroups}
        confirming={isReplacingFleet}
        confirmLabel="Confirm & replace fleet"
        confirmingLabel="Replacing fleet…"
        description="Each unique domicile must use one address. Confirming will replace the current fleet data after we verify each address."
        onCancel={() => {
          if (!isReplacingFleet) setAddressDialogOpen(false);
        }}
        onConfirm={replaceFleetWithAddresses}
      />
    </div>
  );
}

export default AdminSetDefaults;
